import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AUTH_STORAGE_KEY,
  AdminLoginRequest,
  AuthSession,
  LoginResponse,
} from './auth.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly session = signal<AuthSession | null>(this.loadSession());

  readonly isAuthenticated = signal(this.session() !== null);
  readonly fullName = computed(() => this.session()?.fullName ?? '');
  readonly initials = computed(() => {
    const name = this.session()?.fullName?.trim();
    if (!name) {
      return 'A';
    }

    return name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('');
  });

  login(userName: string, password: string): Observable<LoginResponse> {
    const body: AdminLoginRequest = { userName, password };
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/auth/admin/login`, body)
      .pipe(tap((response) => this.setSession(response)));
  }

  logout(): void {
    localStorage.removeItem(AUTH_STORAGE_KEY);
    this.session.set(null);
    this.isAuthenticated.set(false);
    this.router.navigate(['/login']);
  }

  getAccessToken(): string | null {
    return this.session()?.accessToken ?? null;
  }

  private setSession(response: LoginResponse): void {
    const session: AuthSession = {
      accessToken: response.accessToken,
      refreshToken: response.refreshToken,
      expiresIn: response.expiresIn,
      userId: response.userId,
      userType: response.userType,
      fullName: response.fullName,
      email: response.email,
    };
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session));
    this.session.set(session);
    this.isAuthenticated.set(true);
  }

  private loadSession(): AuthSession | null {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as AuthSession;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  }
}
